import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetPtoPprTkDraftQuery, type PtoPprTkDraft } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Loader, Progress } from '@shared/ui/Progress';
import { Banner, Muted, Tab, Tabs, errorText, fmtDateTime } from '../kfo/ui';
import { AuditTab } from './AuditTab';
import { PassportTab } from './PassportTab';
import { SectionsTab } from './SectionsTab';
import {
  GEN_STEPS,
  STALL_TEXT,
  blockingFindings,
  currentStepIndex,
  isRdBusy,
  isRdStalled,
  plural,
  progressLabel,
  progressPercent,
} from './model';
import { ProgressWrap, Small, StatusLine, Step, Steps, Toolbar, type StepState } from './ui';
import { CONFIRM_RESTART, CONFIRM_RESTART_STALLED, type RdActions } from './useRdActions';

type TabKey = 'sections' | 'passport' | 'audit';

/** Этапы конвейера с отметкой текущего (при сбое — этапа, на котором упало). */
function GenSteps({ draft, failed }: { draft: PtoPprTkDraft; failed?: boolean }) {
  const p = draft.gen_progress;
  const cur = currentStepIndex(p?.stage);
  return (
    <Steps data-ppr="gen-steps">
      {GEN_STEPS.map(([key, label], i) => {
        const state: StepState =
          i < cur ? 'done' : i === cur ? (failed ? 'failed' : 'current') : 'todo';
        const round = i === cur && (key === 'audit' || key === 'fix') && p?.round;
        return (
          <Step key={key} $state={state} aria-current={i === cur ? 'step' : undefined}>
            {label}
            {round ? ` · круг ${round}` : ''}
          </Step>
        );
      })}
    </Steps>
  );
}

/**
 * Черновик rd в раскрытой строке списка. Список лёгкий (без разделов и паспорта
 * РД), поэтому содержимое — из GET черновика: опрос раз в 3 с, пока идёт
 * генерация или цикл аудитора, и перечитывание, когда в списке сменились статус
 * или updated_at. Пока черновик занят — только ход работы (содержимое не
 * выдаётся до утверждения, §2 П5). Готов — вкладки «Разделы» / «Паспорт РД» /
 * «Аудит» и кнопки документа.
 */
export function RdDraftView({
  listDraft,
  canWrite,
  actions,
  now,
  onRefreshList,
}: {
  /** Черновик из списка: статусы и ход работы, без разделов и паспорта РД. */
  listDraft: PtoPprTkDraft;
  canWrite: boolean;
  actions: RdActions;
  /** Текущее время (тикает в строке списка) — для признака «генерация зависла». */
  now: number;
  /** Перечитать список черновиков. */
  onRefreshList: () => PromiseLike<unknown>;
}) {
  const [tab, setTab] = useState<TabKey>('sections');
  // Своё состояние, а не isFetching: тот мигал бы на каждом опросе раз в 3 с.
  const [refreshing, setRefreshing] = useState(false);
  const [jump, setJump] = useState<string | null>(null);
  const onJumped = useCallback(() => setJump(null), []);
  const onJump = useCallback((key: string) => {
    setTab('sections');
    setJump(key);
  }, []);

  const [poll, setPoll] = useState(() => isRdBusy(listDraft));
  const card = useGetPtoPprTkDraftQuery(listDraft.id, {
    pollingInterval: poll ? 3000 : 0,
    refetchOnMountOrArgChange: true,
  });
  const full = card.data?.draft;
  // Ход работы и сбой можно показать и по списку, пока карточка грузится;
  // вкладки — только по карточке.
  const draft = full ?? listDraft;
  const busy = isRdBusy(draft);
  useEffect(() => setPoll(busy), [busy]);

  // Список опрашивается отдельно и может раньше карточки увидеть смену статуса —
  // перечитываем карточку: после генерации вкладки покажут свежие разделы.
  const { refetch } = card;
  const listSig = `${listDraft.gen_status}|${listDraft.review?.status ?? ''}|${listDraft.updated_at}`;
  const seenSig = useRef(listSig);
  useEffect(() => {
    if (seenSig.current === listSig) return;
    seenSig.current = listSig;
    void refetch();
  }, [listSig, refetch]);

  async function refresh() {
    setRefreshing(true);
    try {
      await Promise.all([refetch(), onRefreshList()]);
    } finally {
      setRefreshing(false);
    }
  }

  if (busy) {
    const percent = progressPercent(draft);
    const started = draft.gen_progress?.started_at;
    const stalled = isRdStalled(draft, now);
    return (
      <div data-ppr="progress">
        <GenSteps draft={draft} />
        {stalled && (
          <Banner $tone="warning" data-ppr="stalled">
            {STALL_TEXT}. Перезапуск начнёт генерацию заново; разделы, которые правил инженер,
            сохранятся.
          </Banner>
        )}
        <StatusLine>
          {!stalled && <Loader />}
          <b>{progressLabel(draft)}</b>
          {percent != null && (
            <ProgressWrap>
              <Progress percent={percent} />
            </ProgressWrap>
          )}
        </StatusLine>
        <Muted>
          Документ откроется, когда агент-аудитор его утвердит или завершит проверку.
          {started ? ` Запущено ${fmtDateTime(started)}.` : ''}
        </Muted>
        <Toolbar>
          <Button
            $variant="secondary"
            $size="sm"
            type="button"
            disabled={refreshing}
            onClick={refresh}
          >
            {refreshing ? 'Обновление…' : 'Обновить'}
          </Button>
          {stalled && canWrite && (
            <Button
              $variant="primary"
              $size="sm"
              type="button"
              disabled={actions.restarting}
              onClick={() => actions.restart(CONFIRM_RESTART_STALLED)}
            >
              {actions.restarting ? 'Запуск…' : 'Перезапустить'}
            </Button>
          )}
        </Toolbar>
        {actions.error && <Banner $tone="danger">{actions.error}</Banner>}
      </div>
    );
  }

  if (draft.gen_status === 'failed') {
    return (
      <div data-ppr="failed">
        <GenSteps draft={draft} failed />
        <Banner $tone="danger">
          Генерация не завершилась: {draft.gen_error || 'причина не указана'}.
        </Banner>
        {actions.error && <Banner $tone="danger">{actions.error}</Banner>}
        {canWrite && (
          <Toolbar>
            <Button
              $variant="primary"
              $size="sm"
              type="button"
              disabled={actions.restarting}
              onClick={() => actions.restart(null)}
            >
              {actions.restarting ? 'Запуск…' : 'Перезапустить генерацию'}
            </Button>
            <Button
              $variant="ghost"
              $size="sm"
              type="button"
              disabled={actions.deleting}
              onClick={actions.remove}
            >
              Удалить
            </Button>
          </Toolbar>
        )}
      </div>
    );
  }

  if (!full) {
    return card.error ? (
      <Banner $tone="danger">
        {errorText(card.error, 'Не удалось загрузить черновик.')}{' '}
        <Button $variant="secondary" $size="sm" type="button" onClick={() => void refetch()}>
          Повторить
        </Button>
      </Banner>
    ) : (
      <Muted data-ppr="loading">Загрузка черновика…</Muted>
    );
  }

  const review = draft.review;
  const approved = review?.status === 'approved';
  const blocking = blockingFindings(review).length;
  const findingsTotal = review?.findings?.length ?? 0;

  return (
    <div data-ppr="ready">
      {approved ? (
        <Banner $tone="success">
          Утверждён ИИ-аудитором{review.reviewed_at ? ` ${fmtDateTime(review.reviewed_at)}` : ''}.
          Документ требует подписи ответственных лиц.
        </Banner>
      ) : review?.status === 'not_approved' ? (
        <Banner $tone="danger">
          Аудитор не утвердил документ
          {blocking > 0
            ? `: ${blocking} ${plural(blocking, ['блокирующее замечание', 'блокирующих замечания', 'блокирующих замечаний'])}`
            : ''}{' '}
          (вкладка «Аудит»). Чистовой DOCX недоступен — исправьте разделы или дополните исходные
          данные и запустите «Проверить заново».
        </Banner>
      ) : review?.status === 'stale' ? (
        <Banner $tone="warning">
          Документ изменён после утверждения — запустите «Проверить заново», чтобы аудитор
          подтвердил правки.
        </Banner>
      ) : review?.status === 'failed' ? (
        <Banner $tone="danger">
          Аудит не выполнен{review.error ? `: ${review.error}` : ''}. Документ не утверждён —
          запустите «Проверить заново».
        </Banner>
      ) : (
        <Banner $tone="info">Аудит документа не проводился.</Banner>
      )}

      <Toolbar>
        <Button
          $variant="secondary"
          $size="sm"
          type="button"
          disabled={actions.exporting}
          onClick={() => actions.exportDocx(false)}
        >
          DOCX (черновик)
        </Button>
        <span title={approved ? undefined : 'Доступно после утверждения аудитором'}>
          <Button
            $variant="primary"
            $size="sm"
            type="button"
            disabled={!approved || actions.exporting}
            onClick={() => actions.exportDocx(true)}
          >
            DOCX (чистовой)
          </Button>
        </span>
        {!approved && <Small>чистовой — после утверждения аудитором</Small>}
        {canWrite && (
          <>
            <Button
              $variant="secondary"
              $size="sm"
              type="button"
              disabled={actions.reviewing}
              onClick={actions.review}
            >
              {actions.reviewing ? 'Запуск…' : 'Проверить заново'}
            </Button>
            <Button
              $variant="ghost"
              $size="sm"
              type="button"
              disabled={actions.restarting}
              onClick={() => actions.restart(CONFIRM_RESTART)}
            >
              Перезапустить генерацию
            </Button>
            <Button
              $variant="ghost"
              $size="sm"
              type="button"
              disabled={actions.deleting}
              onClick={actions.remove}
            >
              Удалить
            </Button>
          </>
        )}
      </Toolbar>
      {actions.error && <Banner $tone="danger">{actions.error}</Banner>}

      <Tabs>
        <Tab type="button" $active={tab === 'sections'} onClick={() => setTab('sections')}>
          Разделы
        </Tab>
        <Tab type="button" $active={tab === 'passport'} onClick={() => setTab('passport')}>
          Паспорт РД
        </Tab>
        <Tab type="button" $active={tab === 'audit'} onClick={() => setTab('audit')}>
          Аудит{findingsTotal > 0 ? ` (${findingsTotal})` : ''}
        </Tab>
      </Tabs>
      {tab === 'sections' && (
        <SectionsTab draft={draft} canWrite={canWrite} jumpKey={jump} onJumped={onJumped} />
      )}
      {tab === 'passport' && <PassportTab draft={draft} />}
      {tab === 'audit' && <AuditTab draft={draft} onJump={onJump} />}
    </div>
  );
}
